import React, { useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useSheetStore } from "@/lib/store/Sheets";
import { sheetRegistry } from "./sheet-registry";
import "./sheet-styles.css";
import { ChevronLeft, X } from "lucide-react";

interface SheetProviderProps {
	onPendingChangeWarning?: (message: string) => void;
}

export const SheetProvider: React.FC<SheetProviderProps> = ({
	onPendingChangeWarning,
}) => {
	const { sheets, closeSheet, closeAllSheets } = useSheetStore((state) => ({
		sheets: state.sheets,
		closeSheet: state.closeSheet,
		closeAllSheets: state.closeAllSheets,
	}));
	const [closingSheets, setClosingSheets] = useState<Set<string>>(new Set());

	if (!sheets.length) {
		return null;
	}

	return (
		<>
			{sheets.map((sheetInstance) => {
				const registered =
					sheetRegistry[sheetInstance.sheetContentType];
				if (!registered) {
					return null;
				}
				const ContentComponent = registered.contentComponent;
				const HeaderComponent = registered.headerComponent;

				const handleClose = () => {
					if (sheetInstance.isPendingChange) {
						onPendingChangeWarning?.(
							"Die Änderungen wurden noch nicht gespeichert."
						);
						return;
					}
					setClosingSheets((prev) =>
						new Set(prev).add(sheetInstance.key)
					);
					setTimeout(() => {
						closeSheet(sheetInstance.key);
					}, 300);
				};

				const handleCloseAll = () => {
					let hasPendingChanges = false;
					sheets.forEach((sheet) => {
						if (sheet.isPendingChange) {
							hasPendingChanges = true;
							onPendingChangeWarning?.(
								"Es stehen noch Änderungen aus, die nicht gespeichert wurden."
							);
						}
					});
					if (!hasPendingChanges) {
						setClosingSheets(
							new Set(sheets.map((sheet) => sheet.key))
						);
						setTimeout(() => {
							closeAllSheets();
						}, 300);
					}
				};

				const headerClass =
					sheetInstance.sheetContentType === "search"
						? "sheet-header sheet-header-search"
						: "sheet-header sheet-header-default";

				return (
					<DialogPrimitive.Root
						key={sheetInstance.key}
						open={!closingSheets.has(sheetInstance.key)}
						onOpenChange={() => handleClose()}
					>
						<DialogPrimitive.Portal>
							<DialogPrimitive.Content
								className="sheet-container"
								onEscapeKeyDown={(e) => {
									e.preventDefault();
									e.stopPropagation();
									handleClose();
								}}
							>
								<div className={headerClass}>
									<button
										className="sheet-button-icon"
										onClick={() => handleClose()}
									>
										<ChevronLeft className="w-5 h-5" />
										Zurück
									</button>
									<div className="flex flex-row gap-2 items-center justify-center">
										{HeaderComponent && (
											<HeaderComponent
												sheetKey={sheetInstance.key}
												{...sheetInstance.props}
												className="sheet-button-icon"
											/>
										)}
										<button
											className="sheet-button-icon"
											onClick={() => handleCloseAll()}
										>
											<X className="w-5 h-5" />
										</button>
									</div>
								</div>
								<div className="sheet-content">
									<ContentComponent
										sheetKey={sheetInstance.key}
										{...sheetInstance.props}
									/>
								</div>
							</DialogPrimitive.Content>
						</DialogPrimitive.Portal>
					</DialogPrimitive.Root>
				);
			})}
		</>
	);
};
